import { NotFoundError } from '../../../errors.js';
import type { SqlRunner } from '../../../lib/db.js';
import { FILE_BUCKETS } from '../../constants.js';
import type { AIProcessingJob } from '../../contracts/schemas/ai-processing-job.js';
import type {
  ChatWithFileBody,
  ChatWithFileResponse,
} from '../../contracts/schemas/chat-with-file.js';
import type { FileStatistics } from '../../contracts/schemas/file-statistics.js';
import { type AdminFileRow, getFileByIdDb } from '../db/get-by-id.js';
import {
  type AdminFileListRow,
  type ListAdminFilesParams,
  listAdminFilesDb,
} from '../db/list-admin.js';
import {
  type ListFileAiProcessingJobsParams,
  listAiProcessingJobsDb,
} from '../db/list-ai-processing-jobs.js';
import {
  countFilesByGroupDb,
  countFilesByProcessingStageDb,
} from '../db/statistics.js';

const byCountDesc = (a: { count: number }, b: { count: number }) =>
  b.count - a.count;

/**
 * Signs download links that can only read. Injected as `fileHelper`: this
 * module's `FilesService.getSignedUrl` hands out a read-write link, which
 * would let whoever holds it overwrite the stored file.
 */
export interface ReadOnlyFileSigner {
  getReadOnlySignedUrl(params: {
    id: string;
    bucketName: string;
    url: string;
    expires?: number;
  }): Promise<string>;
}

export interface FilesAdminServiceDeps {
  db: SqlRunner;
  cdnUri: string;
  fileHelper: ReadOnlyFileSigner;
}

export type AdminFile = AdminFileRow & { previewUrl: string | null };

/**
 * Answers a question about a document. Supplied by the caller: the model call
 * is backend code (an agent pipeline with its own provider config), and so is
 * loading the file's bytes out of object storage.
 *
 * Takes the file id rather than its text. The model reads the document
 * itself now, so there is no extracted-text column to hand over — and the one
 * that used to exist is no longer written.
 */
export type AskAboutDocument = (params: {
  fileId: string;
  question: string;
}) => Promise<string>;

/** How long an admin preview link stays valid. */
const PREVIEW_EXPIRY_SECONDS = 60 * 60;

/** The buckets the CDN cannot serve: their files are previewed through a signed link. */
const SIGNED_BUCKETS: string[] = [FILE_BUCKETS.PRIVATE, FILE_BUCKETS.INCOMING];

/** Admin-only file reads; the route enforces the admin permission. */
export class FilesAdminService {
  constructor(private readonly deps: FilesAdminServiceDeps) {}

  /**
   * One file of any company, with a link to preview it: signed read-only for an
   * hour when it sits in the private or incoming bucket, on the CDN otherwise.
   *
   * @throws NotFoundError `file_not_found`
   */
  async getById(id: string): Promise<AdminFile> {
    const file = await this.requireFile(id);
    return { ...file, previewUrl: await this.previewUrl(file) };
  }

  /**
   * Asks the model a question about one file's extracted text.
   *
   * @throws NotFoundError `file_not_found`
   * @throws ValidationError `file_has_no_text` — nothing was extracted from the
   * file (no text, or empty text), so there is nothing to ask about.
   */
  async chatWithFile(
    { fileId, message }: ChatWithFileBody,
    askAboutDocument: AskAboutDocument,
  ): Promise<ChatWithFileResponse> {
    // Still a 404 for a file that does not exist, but no "does not contain
    // text" rejection: the model reads the file, so a document that never
    // produced a text layer is still answerable.
    await this.requireFile(fileId);

    const answer = await askAboutDocument({ fileId, question: message });

    return {
      fileId,
      question: message,
      response: answer || 'No response from AI',
    };
  }

  /** A page of every company's files, without their extracted text. */
  async list(
    params: ListAdminFilesParams,
  ): Promise<{ data: AdminFileListRow[]; totalCount: number }> {
    const { rows, totalCount } = await listAdminFilesDb(this.deps.db, params);
    return { data: rows, totalCount };
  }

  /** A page of one file's AI processing jobs, each with the queue processor that ran it. */
  async listAiProcessingJobs(
    params: ListFileAiProcessingJobsParams,
  ): Promise<{ data: AIProcessingJob[]; totalCount: number }> {
    const { rows, totalCount } = await listAiProcessingJobsDb(
      this.deps.db,
      params,
    );
    return { data: rows, totalCount };
  }

  /**
   * Every file counted by bucket, file type and content type (largest first,
   * files without a value left out), and what each AI processing stage got
   * through (busiest stage first).
   */
  async getStatistics(): Promise<FileStatistics> {
    const { db } = this.deps;
    const groupRows = await countFilesByGroupDb(db);
    const stageRows = await countFilesByProcessingStageDb(db);

    const statistics: FileStatistics = {
      byBucketName: [],
      byFileType: [],
      byContentType: [],
      // Numbered here because the counts arrive from Postgres as strings.
      byProcessingStage: stageRows
        .map(({ jobType, processedFiles, failedFiles }) => ({
          jobType,
          processedFiles: Number(processedFiles),
          failedFiles: Number(failedFiles),
        }))
        .sort((a, b) => b.processedFiles - a.processedFiles),
    };

    // Exactly one `is*Group` flag is 0 per row: the grouping set it belongs to.
    // Its null-valued row is dropped, as the original `IS NOT NULL` queries did.
    for (const row of groupRows) {
      if (row.isBucketGroup === 0 && row.bucketName !== null) {
        statistics.byBucketName.push({
          name: row.bucketName,
          count: row.count,
        });
      }
      if (row.isFileTypeGroup === 0 && row.fileType !== null) {
        statistics.byFileType.push({ name: row.fileType, count: row.count });
      }
      if (row.isContentTypeGroup === 0 && row.contentType !== null) {
        statistics.byContentType.push({
          name: row.contentType,
          count: row.count,
        });
      }
    }
    statistics.byBucketName.sort(byCountDesc);
    statistics.byFileType.sort(byCountDesc);
    statistics.byContentType.sort(byCountDesc);

    return statistics;
  }

  /** @throws NotFoundError `file_not_found` */
  private async requireFile(id: string): Promise<AdminFileRow> {
    const file = await getFileByIdDb(this.deps.db, id);
    if (!file) {
      throw new NotFoundError({
        code: 'file_not_found',
        message: 'File not found',
        details: { fileId: id },
      });
    }
    return file;
  }

  private async previewUrl(file: AdminFileRow): Promise<string | null> {
    if (!file.url) return null;
    if (!SIGNED_BUCKETS.includes(file.bucketName)) {
      return `${this.deps.cdnUri}/${file.url}`;
    }
    return this.deps.fileHelper.getReadOnlySignedUrl({
      id: file.id,
      bucketName: file.bucketName,
      url: file.url,
      expires: PREVIEW_EXPIRY_SECONDS,
    });
  }
}
